import React from 'react';

const ReturnPage = () => {
  return (
    <div className="return-page">
      <div className="container">
        <div className="content-wrapper">
          <h1>Return Policy</h1>
          
          <div className="content">
            <p><strong>Effective Date:</strong> January 2025</p>
            
            <p>This return policy outlines the process for returning products purchased through our Platform (www.bunny-boba.com). Please note that our products are freshly prepared beverages made to order, which affects our return policy significantly.</p>

            <h2>Nature of Our Products</h2>
            
            <p>Bunny Boba specializes in <strong>freshly prepared beverages</strong> including:</p>
            <ul>
              <li>Customized boba tea drinks</li>
              <li>Fresh fruit teas</li>
              <li>Milk teas with various toppings</li>
              <li>Specialty drinks made to order</li>
              <li>Custom sweetness and ice levels</li>
            </ul>

            <p>Due to the nature of our products being <strong>perishable, customized beverages</strong>, traditional return policies do not apply in the same manner as they would for physical goods.</p>

            <h2>Return Eligibility</h2>

            <h3>1. Items NOT Eligible for Return</h3>
            <p>The following items <strong>cannot be returned</strong> under any circumstances:</p>
            <ul>
              <li><strong>Consumed beverages:</strong> Any drink that has been partially or fully consumed</li>
              <li><strong>Prepared drinks:</strong> Beverages that have been prepared according to your specifications</li>
              <li><strong>Custom orders:</strong> Drinks made with specific customizations (flavors, toppings, sweetness)</li>
              <li><strong>Taste preference issues:</strong> Returns based on personal taste preferences</li>
              <li><strong>Change of mind:</strong> Returns due to changed preferences after order placement</li>
            </ul>

            <h3>2. Items Eligible for Return/Exchange</h3>
            <p>Returns or exchanges may be considered <strong>only</strong> in the following circumstances:</p>
            <ul>
              <li><strong>Wrong order:</strong> Completely different drink than what was ordered</li>
              <li><strong>Quality defects:</strong> Visible contamination or foreign objects</li>
              <li><strong>Damaged packaging:</strong> Spilled or leaking containers due to our error</li>
            </ul>

            <h2>Return Process</h2>

            <h3>1. Immediate Reporting Required</h3>
            <p>Any return request must be reported <strong>within 15 minutes of receiving your order</strong>. This is crucial because:</p>
            <ul>
              <li>Beverages are perishable and quality degrades quickly</li>
              <li>We need to verify the issue while evidence is clear</li>
              <li>Food safety regulations require immediate action</li>
              <li>Replacement preparation takes time</li>
            </ul>

            <h3>2. Return Authorization</h3>
            <p>Before returning any item, you must:</p>
            <ol>
              <li><strong>Contact our staff immediately</strong> (in-store, phone, or app)</li>
              <li><strong>Provide your order number</strong> and receipt</li>
              <li><strong>Describe the issue</strong> in detail with photos if possible</li>
              <li><strong>Wait for return authorization</strong> from our team</li>
              <li><strong>Follow specific return instructions</strong> provided by our staff</li>
            </ol>

            <h3>3. Return Verification</h3>
            <p>Our team will verify the return by:</p>
            <ul>
              <li>Examining the unopened product</li>
              <li>Checking against the original order specifications</li>
              <li>Documenting the issue for quality control</li>
              <li>Determining the appropriate resolution</li>
              <li>Confirming delivery details for replacement orders (30-minute delivery commitment)</li>
            </ul>

            <h2>Return Resolutions</h2>

            <h3>1. Remake/Replacement</h3>
            <p>For eligible returns, we typically offer:</p>
            <ul>
              <li><strong>Immediate remake</strong> of the correct order</li>
              <li><strong>Priority preparation</strong> to minimize wait time</li>
              <li><strong>Quality assurance</strong> to prevent similar issues</li>
              <li><strong>No additional charges</strong> for our errors</li>
              <li><strong>Fast delivery:</strong> Exchange/replacement delivered within 30 minutes</li>
            </ul>

            <h3>2. Store Credit</h3>
            <p>In some cases, we may offer store credit:</p>
            <ul>
              <li>Valid for 3 months from issue date</li>
              <li>Can be used for any menu items</li>
              <li>Applied directly to your account</li>
            </ul>

            <h3>3. Monetary Refund</h3>
            <p>Cash refunds are <strong>rare</strong> and only provided for:</p>
            <ul>
              <li>System errors or double charges</li>
              <li>Significant service failures</li>
              <li>Health and safety concerns</li>
              <li>Cases where remake is not possible</li>
            </ul>

            <h2>Return Conditions</h2>

            <h3>1. Product Condition</h3>
            <p>For any return consideration, the product must be:</p>
            <ul>
              <li><strong>Unopened and untouched</strong></li>
              <li><strong>In original packaging</strong></li>
              <li><strong>Presented with original receipt</strong></li>
              <li><strong>Returned within the 15-minute window</strong></li>
              <li><strong>Clearly showing the reported issue</strong></li>
            </ul>

            <h3>2. Documentation Required</h3>
            <p>For return processing, you must provide:</p>
            <ul>
              <li>Original order receipt or order number</li>
              <li>Photo evidence of the issue (if applicable)</li>
              <li>Contact information for follow-up</li>
              <li>Detailed description of the problem</li>
            </ul>

            <h2>What We Cannot Accept for Return</h2>

            <h3>1. Health and Safety Restrictions</h3>
            <p>For health and safety reasons, we <strong>cannot accept returns</strong> of:</p>
            <ul>
              <li>Any beverage that has been opened or tasted</li>
              <li>Products that have been out of our premises for over 30 minutes</li>
              <li>Items without proper packaging or identification</li>
              <li>Products that may have been tampered with</li>
            </ul>

            <h3>2. Business Policy Restrictions</h3>
            <p>We do not accept returns for:</p>
            <ul>
              <li><strong>Preference issues:</strong> "Too sweet," "too cold," "don't like the flavor"</li>
              <li><strong>Size regrets:</strong> Wishing you ordered a different size</li>
              <li><strong>Topping choices:</strong> Not liking the toppings you selected</li>
              <li><strong>Competitor comparisons:</strong> "It tastes different from another store"</li>
            </ul>

            <h2>Return Processing Timeline</h2>

            <h3>Immediate Resolution</h3>
            <ul>
              <li><strong>In-store returns:</strong> Immediate assessment and resolution</li>
              <li><strong>Delivery returns:</strong> Coordination with delivery partner for pickup</li>
              <li><strong>Remake orders:</strong> 5-15 minutes depending on complexity</li>
              <li><strong>Exchange/Replacement delivery:</strong> Within 30 minutes from approval</li>
              <li><strong>Refund processing:</strong> 3-7 business days for approved cases</li>
            </ul>

            <h3>Exchange/Replacement Delivery Commitment</h3>
            <p>When your return is approved for exchange or replacement:</p>
            <ul>
              <li><strong>Immediate preparation:</strong> Your replacement order gets priority in our preparation queue</li>
              <li><strong>Express delivery:</strong> Delivered within 30 minutes of return approval</li>
              <li><strong>Same delivery method:</strong> Using the same delivery partner as your original order</li>
              <li><strong>No extra charges:</strong> No additional delivery fees for replacement orders</li>
              <li><strong>Quality guarantee:</strong> Extra quality checks to ensure the replacement meets standards</li>
            </ul>

            <h2>Prevention and Quality Assurance</h2>

            <h3>Our Commitment</h3>
            <p>To minimize returns, we:</p>
            <ul>
              <li><strong>Train staff thoroughly</strong> on preparation standards</li>
              <li><strong>Use quality ingredients</strong> and fresh components</li>
              <li><strong>Double-check orders</strong> before packaging</li>
              <li><strong>Maintain cleanliness</strong> and food safety standards</li>
              <li><strong>Listen to feedback</strong> and improve processes</li>
            </ul>

            <h3>Customer Responsibility</h3>
            <p>To ensure order accuracy, please:</p>
            <ul>
              <li><strong>Review your order</strong> carefully before confirming</li>
              <li><strong>Specify preferences clearly</strong> (sweetness, ice, toppings)</li>
              <li><strong>Ask questions</strong> if unsure about ingredients</li>
              <li><strong>Check your order</strong> upon receipt</li>
              <li><strong>Report issues immediately</strong> for fastest resolution</li>
            </ul>

            <h2>Contact Information for Returns</h2>
            
            <div className="contact-section">
              <h3>Return Support</h3>
              <p><strong>In-Store:</strong> Speak directly with our staff</p>
              <p><strong>Business Hours:</strong> Monday - Friday (12:00 AM - 6:00 PM)</p>
              <p><strong>Address:</strong> FLOOR LG TOWN CENTRAL, LGB-08, BLOCK B, C04 SEC 16B, Greater Noida GHORI, Greater Noida, India</p>
              <p><strong>Response Time:</strong> Immediate for in-store, within 24 hours for remote requests</p>
            </div>

            <h2>Legal and Policy Notes</h2>
            
            <p>This return policy is designed to balance customer satisfaction with the practical realities of our fresh beverage business. We are committed to providing high-quality products and will work with customers to resolve legitimate issues promptly and fairly.</p>

            <p>This policy may be updated to reflect changes in business practices or regulatory requirements. Continued use of our services constitutes acceptance of the current policy.</p>

            <div className="important-note">
              <h3>Important Reminder</h3>
              <p>The best way to ensure satisfaction is communication before and during your order. Our staff is trained to help you choose the right drink and customize it to your preferences. When in doubt, ask questions!</p>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .return-page {
          min-height: 100vh;
          background-color: #fff8f0;
          padding: 40px 0 80px;
        }

        .content-wrapper {
          max-width: 800px;
          margin: 0 auto;
          background: white;
          padding: 40px;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        h1 {
          color: #C2185B;
          text-align: center;
          margin-bottom: 30px;
          font-size: 2.5rem;
        }

        h2 {
          color: #C2185B;
          margin-top: 40px;
          margin-bottom: 20px;
          font-size: 1.8rem;
          border-bottom: 2px solid #C2185B;
          padding-bottom: 10px;
        }

        h3 {
          color: #C2185B;
          margin-top: 30px;
          margin-bottom: 15px;
          font-size: 1.3rem;
        }

        .content p {
          margin-bottom: 15px;
          line-height: 1.6;
          color: #333;
          text-align: justify;
        }

        .content ul, .content ol {
          margin-bottom: 20px;
          padding-left: 25px;
        }

        .content li {
          margin-bottom: 8px;
          line-height: 1.6;
          color: #333;
        }

        .content strong {
          color: #C2185B;
          font-weight: 600;
        }

        .contact-section {
          background: #f8f9fa;
          padding: 25px;
          border-radius: 10px;
          border-left: 4px solid #C2185B;
          margin: 30px 0;
        }

        .contact-section h3 {
          margin-top: 0;
          color: #C2185B;
        }

        .contact-section p {
          margin-bottom: 10px;
          text-align: left;
        }

        .important-note {
          background: linear-gradient(135deg, #fff0f5, #fce4ec);
          padding: 25px;
          border-radius: 15px;
          border: 2px solid #C2185B;
          margin-top: 40px;
        }

        .important-note h3 {
          margin-top: 0;
          color: #C2185B;
          text-align: center;
        }

        .important-note p {
          text-align: center;
          font-weight: 500;
          color: #333;
        }

        @media (max-width: 768px) {
          .content-wrapper {
            margin: 0 20px;
            padding: 30px 20px;
          }

          h1 {
            font-size: 2rem;
          }

          h2 {
            font-size: 1.5rem;
          }

          h3 {
            font-size: 1.2rem;
          }

          .content p, .content li {
            text-align: left;
          }
        }
      `}</style>
    </div>
  );
};

export default ReturnPage; 